import React, { useEffect, useState } from 'react';
import Loading from '@components/Loading';
import {
  Card,
  Spin,
  Row,
  Col,
  Modal,
  Table,
  Button,
  Space,
  Typography,
  message,
  Form,
  Radio,
  DatePicker,
  Input
} from 'antd';
import {
  WarningOutlined,
  ThunderboltOutlined,
  StopOutlined,
  RetweetOutlined,
  RiseOutlined,
  AlertOutlined,
  CheckOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  FullscreenOutlined,
  FullscreenExitOutlined,
  DisconnectOutlined,
  FormOutlined,
  ToolOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import {
  apiGetWarningStatisticsByType,
  apiGetWarningsByTypeAndDate,
  apiResolvedWarning
} from '@services/ManageDevices/DeviceServices';
import { showError, showSuccess } from '@components/notification';
import moment from 'moment';
import { usePermission } from '@contexts/Auth/UsePermission';
import InputUploadImage from '@components/Upload/Upload';
import dayjs from 'dayjs';
import { formatWarningOccurrenceDate, formatCurrency } from '@utils/utils';
import LineIcon from './LineIcon';

const { TextArea } = Input;

const formatNumber = (value) => formatCurrency(value || 0, 0);

// Blinking warning badge from the previous dashboard version (animation in index.css)
const WarningBadge = () => (
  <svg
    className="warning-svg"
    width="30"
    height="27"
    viewBox="0 0 45 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true">
    <path
      d="M43.7602 11.6036C43.5959 11.196 43.2108 10.9347 42.754 10.8576L42.7573 10.8434L36.5994 10.4438L37.8428 4.96555L37.8308 4.9645C37.8533 4.75028 37.8181 4.54084 37.7283 4.3531C37.6384 4.16537 37.4963 4.00461 37.3135 3.88382C37.1296 3.76376 36.9103 3.6873 36.6733 3.66063C36.4362 3.63395 36.1882 3.65782 35.9492 3.73029L35.9443 3.71831L29.987 6.11987L27.9936 1.17495L27.9814 1.17985C27.8791 0.998086 27.7261 0.845608 27.5346 0.734745C27.3431 0.623881 27.1185 0.557742 26.879 0.541678C26.6389 0.526217 26.3907 0.561684 26.1547 0.645175C25.9188 0.728665 25.7017 0.857809 25.5214 1.02205L25.5099 1.01436L21.2808 5.64993L16.6879 2.63258L16.6794 2.64188C16.4798 2.5413 16.2498 2.48666 16.008 2.48239C15.7662 2.47813 15.5193 2.52435 15.2874 2.61733C14.7978 2.81471 14.4475 3.18878 14.301 3.60228L14.2851 3.60119L13.0487 9.04881L6.9792 8.65475L6.97664 8.66595C6.73322 8.67349 6.48793 8.73132 6.26059 8.83478C6.03325 8.93823 5.83025 9.08441 5.66799 9.26149C5.50632 9.43937 5.39025 9.64305 5.32916 9.85604C5.26808 10.069 5.26372 10.2853 5.31643 10.4874L5.30149 10.4934L7.2832 15.4093L1.41212 17.7761L1.41602 17.7858C1.19396 17.8994 0.999072 18.0542 0.847117 18.2377C0.695161 18.4211 0.5904 18.6281 0.5413 18.8419C0.493317 19.0559 0.50251 19.2704 0.568133 19.4682C0.633756 19.6659 0.753965 19.8412 0.919026 19.9799L0.9089 19.9915L5.49832 23.0065L1.3081 27.5996L1.31772 27.6059C1.17654 27.7952 1.08431 28.0055 1.04847 28.2199C1.01264 28.4342 1.03421 28.6465 1.11144 28.8396C1.27575 29.2472 1.66104 29.509 2.1177 29.5856L2.11441 29.5998L8.27231 29.9994L7.02889 35.4777L7.04071 35.4783C7.01824 35.6925 7.05336 35.902 7.14323 36.0897C7.2331 36.2774 7.3752 36.4382 7.55804 36.559C7.7419 36.679 7.96122 36.7555 8.19825 36.7822C8.43527 36.8088 8.68333 36.785 8.92232 36.7125L8.92733 36.7249L14.8842 34.3236L16.8774 39.2681L16.8896 39.2632C17.092 39.6185 17.4866 39.8684 17.9919 39.9013C18.2321 39.9164 18.4801 39.8807 18.716 39.7973C18.9519 39.7138 19.169 39.5849 19.3496 39.421L19.361 39.4287L23.5901 34.7931L28.183 37.8104L28.1915 37.8011C28.3911 37.9017 28.6211 37.9564 28.8629 37.9606C29.1047 37.9649 29.3516 37.9187 29.5836 37.8257C30.0732 37.6283 30.4234 37.2542 30.5701 36.8412L30.5853 36.8421L31.8217 31.3944L37.8917 31.7883L37.8943 31.7771C38.1377 31.7695 38.383 31.7117 38.6104 31.6082C38.8377 31.5048 39.0407 31.3586 39.2029 31.1815C39.3646 31.0036 39.4807 30.8 39.5418 30.587C39.6029 30.374 39.6072 30.1577 39.5545 29.9556L39.5695 29.9496L37.5879 25.0342L43.4596 22.6672L43.4557 22.6575C43.6777 22.5439 43.8726 22.3891 44.0246 22.2056C44.1765 22.0221 44.2813 21.8151 44.3304 21.6014C44.3784 21.3873 44.3693 21.1726 44.3036 20.9748C44.2379 20.777 44.1177 20.6016 43.9525 20.4629L43.9628 20.4518L39.3734 17.4368L43.5636 12.8436L43.554 12.8373C43.6951 12.648 43.7874 12.4377 43.8232 12.2234C43.859 12.0091 43.8375 11.7968 43.7602 11.6036Z"
      fill="#D5002E"
    />
    <rect x="20" y="11" width="4" height="13" fill="white" />
    <rect x="20" y="26" width="4" height="3" fill="white" />
  </svg>
);

const WarningStatistics = () => {
  const { warning } = usePermission();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_warnings: 0,
    resolved_warnings: 0,
    unresolved_warnings: 0,
    cannot_resolve_warnings: 0,
    demolition: 0,
    demolition_resolved: 0,
    demolition_unresolved: 0,
    demolition_cannot_resolve: 0,
    magnetic: 0,
    magnetic_resolved: 0,
    magnetic_unresolved: 0,
    magnetic_cannot_resolve: 0,
    device_offline: 0,
    device_offline_resolved: 0,
    device_offline_unresolved: 0,
    device_offline_cannot_resolve: 0,
    reverse_flow: 0,
    reverse_flow_resolved: 0,
    reverse_flow_unresolved: 0,
    reverse_flow_cannot_resolve: 0,
    leakage: 0,
    leakage_resolved: 0,
    leakage_unresolved: 0,
    leakage_cannot_resolve: 0,
    over_flow: 0,
    over_flow_resolved: 0,
    over_flow_unresolved: 0,
    over_flow_cannot_resolve: 0,
    abnormal_consumption: 0,
    abnormal_consumption_resolved: 0,
    abnormal_consumption_unresolved: 0,
    abnormal_consumption_cannot_resolve: 0,
    manual_meter_reading: 0,
    manual_meter_reading_resolved: 0,
    manual_meter_reading_unresolved: 0,
    manual_meter_reading_cannot_resolve: 0
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [warningList, setWarningList] = useState([]);
  const [currentWarningType, setCurrentWarningType] = useState('');

  // Resolution form modal states
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [resolutionModalVisible, setResolutionModalVisible] = useState(false);
  const [resolutionLoading, setResolutionLoading] = useState(false);
  const [currentWarning, setCurrentWarning] = useState(null);
  const [resolutionForm] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiGetWarningStatisticsByType({
        date: moment().format('YYYY-MM-DD')
      });
      if (res.data?.data) {
        const data = res.data.data;
        setStats({
          total_warnings: parseInt(data.total_warnings || 0),
          resolved_warnings: parseInt(data.resolved_warnings || 0),
          unresolved_warnings: parseInt(data.unresolved_warnings || 0),
          cannot_resolve_warnings: parseInt(data.cannot_resolve_warnings || 0),
          demolition: parseInt(data.demolition || 0),
          demolition_resolved: parseInt(data.demolition_resolved || 0),
          demolition_unresolved: parseInt(data.demolition_unresolved || 0),
          demolition_cannot_resolve: parseInt(data.demolition_cannot_resolve || 0),
          magnetic: parseInt(data.magnetic || 0),
          magnetic_resolved: parseInt(data.magnetic_resolved || 0),
          magnetic_unresolved: parseInt(data.magnetic_unresolved || 0),
          magnetic_cannot_resolve: parseInt(data.magnetic_cannot_resolve || 0),
          device_offline: parseInt(data.device_offline || 0),
          device_offline_resolved: parseInt(data.device_offline_resolved || 0),
          device_offline_unresolved: parseInt(data.device_offline_unresolved || 0),
          device_offline_cannot_resolve: parseInt(data.device_offline_cannot_resolve || 0),
          reverse_flow: parseInt(data.reverse_flow || 0),
          reverse_flow_resolved: parseInt(data.reverse_flow_resolved || 0),
          reverse_flow_unresolved: parseInt(data.reverse_flow_unresolved || 0),
          reverse_flow_cannot_resolve: parseInt(data.reverse_flow_cannot_resolve || 0),
          leakage: parseInt(data.leakage || 0),
          leakage_resolved: parseInt(data.leakage_resolved || 0),
          leakage_unresolved: parseInt(data.leakage_unresolved || 0),
          leakage_cannot_resolve: parseInt(data.leakage_cannot_resolve || 0),
          over_flow: parseInt(data.over_flow || 0),
          over_flow_resolved: parseInt(data.over_flow_resolved || 0),
          over_flow_unresolved: parseInt(data.over_flow_unresolved || 0),
          over_flow_cannot_resolve: parseInt(data.over_flow_cannot_resolve || 0),
          abnormal_consumption: parseInt(data.abnormal_consumption || 0),
          abnormal_consumption_resolved: parseInt(data.abnormal_consumption_resolved || 0),
          abnormal_consumption_unresolved: parseInt(data.abnormal_consumption_unresolved || 0),
          abnormal_consumption_cannot_resolve: parseInt(
            data.abnormal_consumption_cannot_resolve || 0
          ),
          manual_meter_reading: parseInt(data.manual_meter_reading || 0),
          manual_meter_reading_resolved: parseInt(data.manual_meter_reading_resolved || 0),
          manual_meter_reading_unresolved: parseInt(data.manual_meter_reading_unresolved || 0),
          manual_meter_reading_cannot_resolve: parseInt(
            data.manual_meter_reading_cannot_resolve || 0
          )
        });
      }
    } catch (error) {
      console.error('Error fetching warning statistics:', error);
      showError('Không thể tải thống kê cảnh báo');
    } finally {
      setLoading(false);
    }
  };

  const fetchWarningDetails = async (warningType, label) => {
    setModalLoading(true);
    setModalTitle(label);
    setCurrentWarningType(warningType);
    setModalVisible(true);

    try {
      const params = {
        page: 1,
        size: 100,
        date: moment().format('YYYY-MM-DD'),
        warningType: warningType
      };

      const res = await apiGetWarningsByTypeAndDate(params);
      setWarningList(res.data?.data || []);
    } catch (error) {
      console.error('Error fetching warning details:', error);
      showError('Không thể tải chi tiết cảnh báo');
      setWarningList([]);
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Same order and per-type tint as the previous dashboard (2 rows × 3)
  const card = (type, label, icon, tone, key) => ({
    type,
    label,
    icon,
    tone,
    value: stats[key],
    resolved: stats[`${key}_resolved`],
    unresolved: stats[`${key}_unresolved`],
    cannotResolve: stats[`${key}_cannot_resolve`]
  });
  const warningCards = [
    card('DEVICE_OFFLINE', 'Mất kết nối', 'wifi-off', 'offline', 'device_offline'),
    card(
      'MANUAL_METER_READING',
      'Cần đọc chỉ số thủ công',
      'clipboard-check',
      'manual',
      'manual_meter_reading'
    ),
    card('DEMOLITION', 'Tấn công tháo rời', 'shield-alert', 'demolition', 'demolition'),
    card('MAGNETIC', 'Tấn công từ tính', 'magnet', 'magnetic', 'magnetic'),
    card('LEAKAGE', 'Rò rỉ nước', 'droplets', 'leakage', 'leakage'),
    card(
      'ABNORMAL_CONSUMPTION',
      'Tiêu thụ bất thường',
      'trending-up',
      'abnormal',
      'abnormal_consumption'
    )
  ];

  const handleResolved = (record) => {
    setCurrentWarning(record);
    resolutionForm.setFieldsValue({
      status: 1,
      resolved_at: dayjs(),
      resolved_image: null,
      remarks: ''
    });
    setResolutionModalVisible(true);
  };

  const handleResolutionSubmit = async () => {
    try {
      const values = await resolutionForm.validateFields();
      setResolutionLoading(true);

      // Prepare resolved_image - InputUploadImage returns a single file object or null
      const resolvedImage = values.resolved_image;
      const imageUrls = resolvedImage?.url ? [resolvedImage.url] : [];

      const payload = {
        status: values.status,
        resolved_at: values.resolved_at
          ? dayjs(values.resolved_at).format('YYYY-MM-DD HH:mm:ss')
          : null,
        resolved_image: JSON.stringify(imageUrls),
        remarks: values.remarks || ''
      };

      await apiResolvedWarning(currentWarning?.id, payload);
      showSuccess({ message: 'Cập nhật thành công!' });
      setResolutionModalVisible(false);
      resolutionForm.resetFields();
      fetchWarningDetails(currentWarningType, modalTitle);
      fetchData(); // Refresh statistics
    } catch (error) {
      console.error('Error resolving warning:', error);
      if (error?.errorFields) {
        showError('Vui lòng điền đầy đủ thông tin bắt buộc');
      } else {
        showError('Không thể cập nhật trạng thái');
      }
    } finally {
      setResolutionLoading(false);
    }
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      key: 'stt',
      width: 60,
      render: (text, record, index) => index + 1
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const statusConfig = {
          0: { icon: CloseCircleFilled, color: '#e11d48', text: 'Chưa xử lý' },
          1: { icon: CheckCircleFilled, color: '#10b981', text: 'Đã xử lý' },
          2: { icon: ExclamationCircleFilled, color: '#f59e0b', text: 'Không xử lý được' }
        };
        const config = statusConfig[record?.status] || statusConfig[0];
        const StatusIcon = config.icon;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <StatusIcon style={{ fontSize: '20px', color: config.color }} />
            <Typography.Text
              style={{
                fontSize: '14px',
                color: config.color
              }}>
              {config.text}
            </Typography.Text>
          </div>
        );
      },
      width: 150
    },
    {
      title: 'Số Sêri',
      dataIndex: 'serial_no',
      key: 'serial_no',
      width: 100
    },
    {
      title: 'Khách hàng',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (text, record) => (
        <>
          <span style={{ color: '#0284c7' }}>{record?.customer_id}</span> - {record?.customer_name}
        </>
      ),
      width: 200
    },
    {
      title: 'CN',
      dataIndex: 'branch',
      key: 'branch',
      width: 100
    },
    {
      title: 'TQL',
      dataIndex: 'manage_group',
      key: 'manage_group',
      width: 100
    },
    {
      title: 'ĐHK',
      dataIndex: 'parent_device',
      key: 'parent_device',
      width: 100
    },
    {
      title: 'Tuyến',
      dataIndex: 'route',
      key: 'route',
      width: 100
    },
    {
      title: 'Tên cảnh báo',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: 'Ngày xảy ra',
      dataIndex: 'date_check',
      key: 'date_check',
      render: (text, record) => (
        <>
          {record?.date_check
            ? formatWarningOccurrenceDate(record?.date_check)
            : moment(record?.created_at)?.format('DD/MM/YYYY HH:mm')}
        </>
      ),
      width: 100
    },
    {
      title: 'Ngày thông báo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text, record) => <>{moment(record?.created_at)?.format('DD/MM/YYYY HH:mm')}</>,
      width: 100
    },
    {
      title: 'Người xử lý',
      dataIndex: 'resolved_by',
      key: 'resolved_by',
      width: 100
    },
    {
      title: 'Thời điểm xử lý',
      dataIndex: 'resolved_at',
      key: 'resolved_at',
      render: (text, record) => (
        <>{record?.resolved_at ? moment(record?.resolved_at).format('DD/MM/YYYY HH:mm') : '-'}</>
      ),
      width: 120
    },
    {
      title: 'Ảnh xử lý',
      dataIndex: 'resolved_image',
      key: 'resolved_image',
      render: (text, record) => {
        // Parse resolved_image if it's a JSON string
        let images = record?.resolved_image;

        // If it's a string, try to parse it
        if (typeof images === 'string' && images.trim().startsWith('[')) {
          try {
            images = JSON.parse(images);
          } catch (e) {
            console.error('Error parsing resolved_image:', e);
            images = null;
          }
        }

        // Check if we have valid images array
        if (images && Array.isArray(images) && images.length > 0) {
          // If only 1 image, open directly in new tab
          if (images.length === 1) {
            return (
              <Button type="link" onClick={() => window.open(images[0], '_blank')}>
                Xem ảnh
              </Button>
            );
          }

          // If multiple images, show modal with clickable images
          return (
            <Button
              type="link"
              onClick={() => {
                Modal.info({
                  title: 'Ảnh xử lý (Click vào ảnh để mở tab mới)',
                  width: 600,
                  content: (
                    <div style={{ marginTop: 16 }}>
                      {images.map((url, index) => (
                        <img
                          key={index}
                          src={url}
                          alt={`Ảnh xử lý ${index + 1}`}
                          onClick={() => window.open(url, '_blank')}
                          style={{
                            width: '100%',
                            marginBottom: 8,
                            borderRadius: 8,
                            cursor: 'pointer',
                            transition: 'transform 0.2s'
                          }}
                          onMouseEnter={(e) => (e.target.style.transform = 'scale(1.02)')}
                          onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
                        />
                      ))}
                    </div>
                  )
                });
              }}>
              Xem ảnh ({images.length})
            </Button>
          );
        }
        return '-';
      },
      width: 100
    },
    {
      title: 'Ghi chú xử lý',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (text, record) => <>{record?.remarks || '-'}</>,
      width: 150
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      render: (text, record) => <>{record?.note}</>,
      width: 200
    },
    {
      title: 'Số ngày tồn tại',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (text, record) => <>{record?.frequency ?? 1}</>,
      width: 200
    },
    ...(currentWarningType === 'MAGNETIC'
      ? [
          {
            title: 'Số lần xảy ra',
            dataIndex: 'occurrence_count',
            key: 'occurrence_count',
            render: (text, record) => <>{record?.occurrence_count ?? 0}</>,
            width: 130
          }
        ]
      : []),
    ...(currentWarningType === 'MANUAL_METER_READING'
      ? [
          {
            title: 'Kỳ tham chiếu',
            dataIndex: 'period_reference',
            key: 'period_reference',
            render: (text, record) =>
              record?.period_reference ? moment(record.period_reference).format('MM/YYYY') : '-',
            width: 100
          }
        ]
      : []),
    {
      title: 'Hành động',
      dataIndex: 'actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          {warning?.updateStatus && record?.status == 0 && (
            <Button
              onClick={() => handleResolved(record)}
              icon={<CheckOutlined />}
              style={{
                backgroundColor: '#f1f5f9',
                color: '#0284c7',
                border: 'none'
              }}>
              Cập nhật trạng thái
            </Button>
          )}
        </Space>
      )
    }
  ];

  if (loading) {
    return (
      <div
        className="loading-container"
        style={{
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <div className="triage__head">
        <div>
          <div className="triage__title">
            <WarningBadge />
            <h3>Cảnh báo & phân loại xử lý sự cố</h3>
            <span className="triage__badge">{formatNumber(stats.total_warnings)} tổng số</span>
          </div>
          <p>Nhấn vào từng thẻ để xem chi tiết và xử lý cảnh báo trong ngày</p>
        </div>
      </div>

      <div className="warn-grid">
        {warningCards.map((item) => {
          const total = item.value || 0;
          const resolved = item.resolved || 0;
          const progress = total ? Math.round((resolved / total) * 100) : 0;
          const open = () => fetchWarningDetails(item.type, `Cảnh báo ${item.label.toLowerCase()}`);
          return (
            <div
              key={item.type}
              className={`warn-tile tone-${item.tone}`}
              role="button"
              tabIndex={0}
              onClick={open}
              onKeyDown={(e) => e.key === 'Enter' && open()}>
              <div className="warn-tile__head">
                <span className="warn-tile__label" title={item.label}>
                  {item.label}
                </span>
                <LineIcon name={item.icon} size={17} className="warn-tile__icon" />
              </div>
              <div className="warn-tile__value">
                {formatNumber(total)}
                {/* <small>vụ việc</small> */}
              </div>
              {total === 0 ? (
                <div className="warn-tile__safe">Không ghi nhận vi phạm</div>
              ) : (
                <>
                  <div className="warn-tile__counts">
                    <span>
                      Đã xử lý: <b className="is-done">{formatNumber(resolved)}</b>
                    </span>
                    <span>
                      Chờ: <b>{formatNumber(item.unresolved || 0)}</b>
                    </span>
                  </div>
                  <div
                    className="warn-tile__bar"
                    title={`${progress}% đã xử lý${
                      item.cannotResolve ? ` · ${item.cannotResolve} không xử lý được` : ''
                    }`}>
                    <span style={{ width: `${progress}%` }} />
                  </div>
                </>
              )}
              <span className="warn-tile__more">
                Xem & xử lý
                <LineIcon name="arrow-right" size={14} />
              </span>
            </div>
          );
        })}
      </div>

      <Modal
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingRight: 32
            }}>
            <span>{modalTitle}</span>
            <Button
              type="text"
              icon={isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setIsFullScreen((prev) => !prev)}
              title={isFullScreen ? 'Thu nhỏ' : 'Toàn màn hình'}
            />
          </div>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setIsFullScreen(false);
        }}
        maskClosable={false}
        width={isFullScreen ? '100vw' : 1000}
        style={isFullScreen ? { top: 0, padding: 0, maxWidth: '100vw' } : {}}
        footer={null}
        bodyStyle={
          isFullScreen
            ? { height: 'calc(100vh - 55px)', overflowY: 'auto', padding: '12px 16px' }
            : { maxHeight: '60vh', overflowY: 'auto' }
        }>
        {modalLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={warningList}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false
            }}
            scroll={{ x: 'max-content', y: isFullScreen ? 'calc(100vh - 220px)' : 380 }}
          />
        )}
      </Modal>

      {/* Resolution Form Modal */}
      <Modal
        title="Cập nhật trạng thái xử lý cảnh báo"
        open={resolutionModalVisible}
        onCancel={() => {
          setResolutionModalVisible(false);
          resolutionForm.resetFields();
        }}
        maskClosable={false}
        onOk={handleResolutionSubmit}
        confirmLoading={resolutionLoading}
        okText="Cập nhật"
        cancelText="Hủy"
        width={600}>
        <Form form={resolutionForm} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item
            name="status"
            label="Trạng thái"
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}>
            <Radio.Group>
              <Radio value={1}>Đã xử lý</Radio>
              <Radio value={2}>Không xử lý được</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="resolved_at"
            label="Thời điểm xử lý"
            rules={[{ required: true, message: 'Vui lòng chọn thời điểm xử lý' }]}>
            <DatePicker
              style={{ width: '100%' }}
              showTime={{ format: 'HH:mm:ss' }}
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Chọn thời điểm"
            />
          </Form.Item>

          <Form.Item name="resolved_image" label="Ảnh xử lý">
            <InputUploadImage
              onChange={(file) => {
                resolutionForm.setFieldsValue({ resolved_image: file });
              }}
            />
          </Form.Item>

          <Form.Item name="remarks" label="Ghi chú">
            <TextArea
              rows={4}
              placeholder="Nhập ghi chú về việc xử lý cảnh báo"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WarningStatistics;
